<template>
    <div class="ca-section">
        <div class="ca-header">
            <div class="ca-header-left">
                <i class="fas fa-file-invoice-dollar ca-header-icon"></i>
                <div>
                    <h3 class="ca-title">Cost Approvals</h3>
                    <p class="ca-subtitle">Overview of pending and recent requests</p>
                </div>
            </div>
            <router-link to="/cost-requests" class="ca-view-all">
                View Board <i class="fas fa-arrow-right"></i>
            </router-link>
        </div>

        <!-- Stats cards row -->
        <div class="ca-stats-grid">
            <div class="ca-stat-card ca-stat-total">
                <div class="ca-stat-icon"><i class="fas fa-layer-group"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">{{ stats.totalRequests }}</span>
                    <span class="ca-stat-label">Total Requests</span>
                </div>
            </div>
            <div class="ca-stat-card ca-stat-pending">
                <div class="ca-stat-icon"><i class="fas fa-clock"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">{{ stats.pendingApproval }}</span>
                    <span class="ca-stat-label">Pending Approval</span>
                </div>
            </div>
            <div class="ca-stat-card ca-stat-approved">
                <div class="ca-stat-icon"><i class="fas fa-check-circle"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">{{ stats.approved }}</span>
                    <span class="ca-stat-label">Approved</span>
                </div>
            </div>
            <div class="ca-stat-card ca-stat-overdue">
                <div class="ca-stat-icon"><i class="fas fa-exclamation-triangle"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">{{ stats.overdue }}</span>
                    <span class="ca-stat-label">Overdue</span>
                </div>
            </div>
            <div class="ca-stat-card ca-stat-amount">
                <div class="ca-stat-icon"><i class="fas fa-indian-rupee-sign"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">₹{{ formattedPendingAmount }}</span>
                    <span class="ca-stat-label">Pending Amount</span>
                </div>
            </div>
            <div class="ca-stat-card ca-stat-bill">
                <div class="ca-stat-icon"><i class="fas fa-receipt"></i></div>
                <div class="ca-stat-info">
                    <span class="ca-stat-value">{{ stats.billsPending }}</span>
                    <span class="ca-stat-label">Bills Pending</span>
                </div>
            </div>
        </div>

        <!-- Recent requests mini-list -->
        <div class="ca-recent" v-if="recentRequests.length">
            <div class="ca-recent-header">
                <span class="ca-recent-title">Recent Requests</span>
            </div>
            <div class="ca-recent-list">
                <router-link class="ca-recent-item" v-for="req of recentRequests" :key="req.id"
                    :to="`/cost-requests/${req.id}`">
                    <div class="ca-ri-left">
                        <span class="ca-ri-status"
                            :style="{ background: req.status && req.status.color ? req.status.color : '#94a3b8' }"></span>
                        <div>
                            <span class="ca-ri-title">{{ req.title }}</span>
                            <span class="ca-ri-meta">{{ req.requestedBy ? req.requestedBy.fullName : '—' }}{{
                                req.department ? ' · ' + req.department.name : '' }}</span>
                        </div>
                    </div>
                    <div class="ca-ri-right">
                        <span class="ca-ri-amount">₹{{ formatAmount(req.amount) }}</span>
                        <span class="ca-ri-date">{{ formatDate(req.createdAt) }}</span>
                    </div>
                </router-link>
            </div>
        </div>

        <!-- Empty state -->
        <div class="ca-empty" v-if="!loading && !recentRequests.length">
            <i class="fas fa-file-invoice"></i>
            <span>No cost requests yet</span>
        </div>
    </div>
</template>

<script>
import { http } from "../../utils/http"

export default {
    data() {
        return {
            stats: {
                totalRequests: 0,
                pendingApproval: 0,
                approved: 0,
                overdue: 0,
                pendingAmount: 0,
                billsPending: 0,
            },
            recentRequests: [],
            loading: false,
        }
    },
    computed: {
        formattedPendingAmount() {
            return Number(this.stats.pendingAmount || 0).toLocaleString("en-IN")
        },
    },
    async mounted() {
        await this.fetchDashboardStats()
    },
    methods: {
        async fetchDashboardStats() {
            this.loading = true
            try {
                const res = await http.get("/cost-approval-requests/dashboard-stats")
                const data = res.data.result || res.data || {}
                this.stats = {
                    totalRequests: data.stats?.totalRequests || 0,
                    pendingApproval: data.stats?.pendingApproval || 0,
                    approved: data.stats?.approved || 0,
                    overdue: data.stats?.overdue || 0,
                    pendingAmount: data.stats?.pendingAmount || 0,
                    billsPending: data.stats?.billsPending || 0,
                }
                this.recentRequests = data.recentRequests || []
            } catch (err) {
                console.error("Error fetching cost approval dashboard stats", err)
            }
            this.loading = false
        },
        formatAmount(val) {
            if (!val) return "0"
            return Number(val).toLocaleString("en-IN")
        },
        formatDate(d) {
            if (!d) return ""
            return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.ca-section {
    background: white;
    border: 1px solid $gray100;
    border-radius: $radiusMd;
    overflow: hidden;
}

/* Header */
.ca-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid $gray100;
}

.ca-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ca-header-icon {
    font-size: 18px;
    color: $primaryColor;
    background: $primaryLight;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ca-title {
    font-size: 15px;
    font-weight: 700;
    color: $gray800;
    margin: 0;
}

.ca-subtitle {
    font-size: 11.5px;
    color: $gray400;
    margin: 0;
}

.ca-view-all {
    font-size: 12.5px;
    font-weight: 600;
    color: $primaryColor;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color $transitionFast;

    i {
        font-size: 10px;
    }

    &:hover {
        color: $primaryDark;
    }
}

/* Stats Grid */
.ca-stats-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 0;
    border-bottom: 1px solid $gray100;
}

@media (max-width: 1200px) {
    .ca-stats-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .ca-stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.ca-stat-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px 18px;
    border-right: 1px solid $gray100;
    transition: background $transitionFast;

    &:last-child {
        border-right: none;
    }

    &:hover {
        background: $gray50;
    }
}

.ca-stat-icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
}

.ca-stat-info {
    display: flex;
    flex-direction: column;
}

.ca-stat-value {
    font-size: 18px;
    font-weight: 800;
    color: $gray800;
    line-height: 1.2;
}

.ca-stat-label {
    font-size: 11px;
    color: $gray400;
    font-weight: 500;
}

/* Stat card color variants */
.ca-stat-total .ca-stat-icon {
    background: #f1f5f9;
    color: #475569;
}

.ca-stat-pending .ca-stat-icon {
    background: #fef3c7;
    color: #d97706;
}

.ca-stat-pending .ca-stat-value {
    color: #d97706;
}

.ca-stat-approved .ca-stat-icon {
    background: #d1fae5;
    color: #059669;
}

.ca-stat-approved .ca-stat-value {
    color: #059669;
}

.ca-stat-overdue .ca-stat-icon {
    background: #fee2e2;
    color: #dc2626;
}

.ca-stat-overdue .ca-stat-value {
    color: #dc2626;
}

.ca-stat-amount .ca-stat-icon {
    background: $primaryLight;
    color: $primaryColor;
}

.ca-stat-amount .ca-stat-value {
    color: $primaryColor;
}

.ca-stat-bill .ca-stat-icon {
    background: #ede9fe;
    color: #7c3aed;
}

.ca-stat-bill .ca-stat-value {
    color: #7c3aed;
}

/* Recent Requests */
.ca-recent {
    padding-bottom: 4px;
}

.ca-recent-header {
    padding: 10px 20px;
}

.ca-recent-title {
    font-size: 11.5px;
    font-weight: 700;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.ca-recent-list {
    display: flex;
    flex-direction: column;
}

.ca-recent-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 20px;
    border-top: 1px solid $gray50;
    text-decoration: none;
    color: inherit;
    transition: background $transitionFast;

    &:hover {
        background: $gray50;
    }
}

.ca-ri-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.ca-ri-status {
    width: 8px;
    height: 8px;
    border-radius: $radiusFull;
    flex-shrink: 0;
}

.ca-ri-title {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: $gray700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 350px;
}

.ca-ri-meta {
    display: block;
    font-size: 11px;
    color: $gray400;
}

.ca-ri-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
}

.ca-ri-amount {
    font-size: 13px;
    font-weight: 700;
    color: $gray800;
}

.ca-ri-date {
    font-size: 11px;
    color: $gray400;
}

/* Empty state */
.ca-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 30px 20px;
    color: $gray300;
    font-size: 13px;

    i {
        font-size: 18px;
    }
}
</style>
